import { useTranslation } from 'react-i18next';
import makeFirstLetterCapital from '../../../lib/helper-functions/makeFirstLetterCapital';
import mcpImg from '../../../assets/mcp.png';
import { useEffect, useRef, useState } from 'react';
import get_all_available_mcp_api_call from '../../../lib/api/mcp/get-all-available-mcp';
import LoadingSpinner from '../../../ui/loadingSpinner/LoadingSpinner';
import get_all_connected_mcp_api_call from '../../../lib/api/mcp/get-all-connected-mcp';
import {
  listMcpConnections,
  McpConnection,
} from '../../../lib/api/control-center/control-center-api';
import Popup from '../../../ui/popup/Popup';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import AINormalInput from '../../../ui/inputs/AINormalInput';
import convertSnakeCaseToTitleCase from '../../../lib/helper-functions/convertSnakeCaseToTitleCase';
import connect_mcp_api_call from '../../../lib/api/mcp/connect-mcp';
import NoData from '../../../ui/noData/NoData';
import update_mcp_api_call from '../../../lib/api/mcp/update-mcp';
import {
  getMcpErrorMessage,
  hasMcpCredentialChanges,
} from '../../../lib/api/mcp/mcp-credentials-payload';
import {
  isMcpSecretField,
  isSavedMcpSecret,
  withoutBlankSavedSecrets,
} from './mcp-secret-fields';
import {
  isOptionalMcpField,
  isTriplewhaleStoreField,
  mcpFieldLabel,
} from './mcp-optional-fields';
import {
  isStripeReportingOnly,
  platformDisplayName,
} from './stripe-reporting';
import delete_mcp_api_call from '../../../lib/api/mcp/delete-mcp';
import { submitLogToBugsnag } from '../../../lib/api/log';
import isNotEmptyString from '../../../lib/helper-functions/isNotEmptyString';
import removeHttpPrefix from '../../../lib/helper-functions/removeHttpPrefix';
import {
  SHOPIFY_MCP_SLUG,
  SHOPIFY_AGENCY_APP_CALLBACK_URL,
  SHOPIFY_AGENCY_APP_URL_ORIGIN,
  SHOPIFY_DOMAIN_FIELD,
  SHOPIFY_TOKEN_FIELD,
  SHOPIFY_CLIENT_ID_FIELD,
  SHOPIFY_CLIENT_SECRET_FIELD,
  ShopifyAuthMode,
  SHOPIFY_DEFAULT_AUTH_MODE,
  SHOPIFY_AUTH_MODE_OPTIONS,
  getShopifyInitialAuthMode,
  getShopifyCredentialFields,
  normalizeShopifyDomain,
  isValidShopifyDomain,
  isValidShopifyToken,
  isValidShopifyClientId,
  isValidShopifyClientSecret,
} from '../../../lib/helper-functions/shopifyIntegration';
import ShopifySetupGuide from './ShopifySetupGuide';
import store from '../../../store/store';
import { setMessage } from '../../../store/reducers/appSlice';
import {
  crossSVG,
  linkSVG,
  saveSVG,
  settingSVG,
  warningSVG,
  reloadArrowsSVG,
} from '../../../lib/allCustomSVGs';
import {
  APP_ENV,
  INTERCOM_ENABLED_ENVS,
} from '../../../lib/constants/central-api';
import { useNavigate } from 'react-router-dom';
import save_mcp_oauth_api_call from '../../../lib/api/mcp/save-mcp-oauth';
import AISelectInput from '../../../ui/inputs/AISelectInput';
import MCPStatusCard from './MCPStatusCard';
import MetaSystemUserSection from '../../controlCenter/MetaSystemUserSection';
import why_are_you_disconnecting_api_call from '../../../lib/api/mcp/why-are-you-disconnecting-api-call';
import DisconnectionReasonPopup from '../../../components/disconnectionReasonPopup/DisconnectionReasonPopup';
import refresh_mcp_accounts_api_call from '../../../lib/api/mcp/refresh-mcp-accounts';
import '../../../styles/pages/MCP/mcpConfigBox.scss';
import SearchSelectInput from '../../../ui/inputs/SearchSelectInput';
import { swapToBundledMcpLogo } from '../../../lib/helper-functions/mcpLogoFallback';
import { customMcpLogoUrl } from '../../../lib/helper-functions/customMcpLogo';
import {
  getConnectUrl,
  getAgencyAppConnectUrl,
} from '../../../lib/api/integrations/integrations-api';
import {
  getMcpConnectionStatusView,
  getMcpCredentialNoticeKey,
  matchesMcpStatusFilter,
  MCP_STATUS_FILTER_ALL,
  isMcpSaveRejected,
} from './mcp-connection-status';
import {
  getCustomOauthPillView,
  matchesCustomServerFilter,
} from './custom-mcp-oauth-status';
import { isOauthConnectUnavailable } from './mcp-oauth-availability';

// Shows the manual "paste a Business-Manager system-user token" CTA on the
// Facebook card (see <MetaSystemUserSection compact /> below), next to the OAuth
// connect. Flip to `false` to hide the paste flow again.
const SHOW_META_SYSTEM_USER_CONNECT = true;

const MCP = ({
  searchValue,
  selectedFilter,
  canManage = true,
  onListLoaded,
}: {
  searchValue: string;
  selectedFilter: string;
  // Called with the platform rows every time the list is (re)loaded: on
  // mount and after each connect, update and disconnect. Lets the host page
  // keep its own totals in step without a reload.
  onListLoaded?: (rows: any[]) => void;
  // W1 — when false (analyst / read_only), the integration cards render
  // read-only: no connect/edit popup, no status toggle, no disconnect. The
  // backend still rejects the write — this is the affordance-hiding half.
  canManage?: boolean;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [mcpList, setMcpList] = useState<any[]>([]);
  const [filteredMcpList, setFilteredMcpList] = useState<any[]>([]);
  // Custom (Control Center) MCP servers. The built-in catalog endpoints
  // (`get_all_available_mcp_api_call` / `get_all_connected_mcp_api_call`) only
  // know the 8 vetted platforms, so custom servers are fetched separately and
  // rendered as read-only cards alongside them (managed on the Governance tab).
  const [customServers, setCustomServers] = useState<McpConnection[]>([]);

  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
  const [editingMCP, setEditingMCP] = useState<any>();
  const [preservedEditingMCP, setPreservedEditingMCP] = useState<any>();
  // The credentials the Configure popup was opened with. An update whose form
  // still matches them sends no credentials at all, so central-api keeps the
  // stored ones (an OAuth platform's list entry carries none to send back).
  const credentialsBaselineRef = useRef<any>(undefined);
  // Why the last save from the popup failed, shown inside the popup.
  const [modalErrorMessage, setModalErrorMessage] = useState<
    string | undefined
  >(undefined);

  const [
    isActivationToggleConfirmationVisible,
    setIsActivationToggleConfirmationVisible,
  ] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDisconnectionReasonPopupOpen, setIsDisconnectionReasonPopupOpen] =
    useState(false);
  const [isSubmittingReason, setIsSubmittingReason] = useState(false);
  const [isRefreshingAccounts, setIsRefreshingAccounts] = useState(false);
  // The integration whose OAuth consent screen the user is about to be sent
  // to. Set on Connect/Configure, cleared on cancel; only "Continue" performs
  // the full-page navigation off the app.
  const [pendingOauthMCP, setPendingOauthMCP] = useState<any>(undefined);

  const [manualAccountFields, setManualAccountFields] = useState<Set<string>>(
    new Set()
  );

  // How the Shopify store connects. NEW connections default to `oauth` — it is
  // the only way to reach a client store (Shopify refuses client_credentials
  // off-org with `shop_not_permitted`, and retired merchant-admin custom apps
  // that issued shpat_ tokens). Re-derived from the saved credentials whenever
  // the popup opens on an EXISTING connection.
  const [shopifyAuthMode, setShopifyAuthMode] = useState<ShopifyAuthMode>(
    SHOPIFY_DEFAULT_AUTH_MODE
  );

  const isTrackingScriptConnectionSuccess =
    store.getState().user?.isTrackingScriptConnectionSuccess ?? undefined;

  const toggleEditPopupModal = (isEditPopupOpen: boolean) => {
    if (isEditPopupOpen === false) {
      setEditingMCP(undefined);
      setPreservedEditingMCP(undefined);
      setManualAccountFields(new Set());
      credentialsBaselineRef.current = undefined;
    }
    setModalErrorMessage(undefined);
    setIsEditPopupOpen(isEditPopupOpen);
    setIsActivationToggleConfirmationVisible(false);
    setIsSubmitting(false);
    setIsDeleting(false);
  };

  const openEditPopup = (mcp: any) => {
    setEditingMCP(mcp);
    credentialsBaselineRef.current = mcp?.credentials;
    toggleEditPopupModal(true);
  };

  // A failed save from the popup is explained inside the popup; when the
  // popup is not open (a keyless platform connects straight from its card)
  // the same text goes to a toast.
  const reportSaveFailure = (response: any) => {
    const message = getMcpErrorMessage(response) ?? `${t('MCP-SaveFailed')}`;
    if (isEditPopupOpen) {
      setModalErrorMessage(message);
    } else {
      store.dispatch(setMessage({ message, messageType: 'error' }));
    }
  };

  useEffect(() => {
    if (isEditPopupOpen && editingMCP?.mcp_slug === SHOPIFY_MCP_SLUG) {
      // A new connection starts in `agency_app` (the agency's own app, the
      // default until Shopify approves ADUP's app); an existing one opens in
      // the mode its saved credentials show.
      setShopifyAuthMode(
        getShopifyInitialAuthMode(
          editingMCP?.is_active !== undefined,
          editingMCP?.credentials
        )
      );
    }
    // Derive only when the popup opens — not on every credentials keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditPopupOpen]);

  useEffect(() => {
    // oAuthRedirectHandler sets isSubmitting=true and then leaves the page for
    // the provider's consent screen. If the user comes BACK with the browser's
    // back button, the page is restored from the back/forward cache with React
    // state frozen mid-flight — isSubmitting stays true and, because every
    // card's `disabled` includes it, the whole Integrations grid is dead until
    // a manual reload. A bfcache restore fires `pageshow` with `persisted`;
    // reset the in-flight flags there. (A full reload resets state anyway, so
    // this is a no-op on the normal OAuth ?code= return.)
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setIsSubmitting(false);
        setIsDeleting(false);
        setPendingOauthMCP(undefined);
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  useEffect(() => {
    //* Verify if the user is redirected from an OAuth consent screen
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');

    if (code && isNotEmptyString(code) && state && isNotEmptyString(state)) {
      //* Removing parameters from browser URL
      window.history.replaceState({}, '', window?.location?.pathname);
      oAuthSaveHandler(code, state);
    } else if (
      urlParams.get('connected') ||
      urlParams.get('integration_error') ||
      urlParams.get('shopify_install')
    ) {
      // central-api's OAuth callback completes the token exchange server-side
      // and 302s back here with a result param (Shopify + the W2 integrations
      // surface). Until now only the happy path was visible (the grid simply
      // showed the connection); a merchant-cancelled consent or a failed
      // exchange landed silently. Surface all three outcomes.
      window.history.replaceState({}, '', window?.location?.pathname);
      handleIntegrationReturn(urlParams);
      getMCPs();
    } else {
      //* Verify if the user is redirected from TARA Chat
      const errorId = urlParams.get('errorId');
      if (errorId) {
        //* Removing parameters from browser URL
        window.history.replaceState({}, '', window?.location?.pathname);
        getMCPs(false, Number(errorId));
      } else {
        getMCPs();
      }
    }
    // Custom (Control Center) servers load independently of the built-in grid.
    getCustomServers();
  }, []);

  useEffect(() => {
    if (mcpList.length > 0) {
      validateAdUpAITrackingMCP();
    }
  }, [isTrackingScriptConnectionSuccess]);

  useEffect(() => {
    // Filter MCPs based on search value first
    const filteredBySearch = searchMCPs(searchValue, mcpList);

    // Apply status filter. The dropdown offers exactly the states
    // `getMcpConnectionStatus` can return, so filtering is the same decision
    // the pill makes — a card can never be filed under a status other than
    // the one it shows.
    let finalFiltered = filteredBySearch;
    if (selectedFilter !== MCP_STATUS_FILTER_ALL) {
      finalFiltered = filteredBySearch?.filter((mcp) =>
        matchesMcpStatusFilter(mcp, selectedFilter)
      );
    }

    setFilteredMcpList(finalFiltered);
  }, [searchValue, selectedFilter, mcpList]);

  const getMCPs = async (
    noLoader: boolean = false,
    errorMcpId: number | undefined = undefined
  ) => {
    function processData(allMCPs: any, connectedMCPs: any) {
      // Defensive: the API helpers return [] on error/no-shop, but never let a
      // non-array freeze the page on .forEach/.map.
      const safeAll = Array.isArray(allMCPs) ? allMCPs : [];
      const safeConnected = Array.isArray(connectedMCPs) ? connectedMCPs : [];
      const connectionMap = new Map();
      safeConnected.forEach((item: any) => {
        connectionMap.set(item.tara_mcp_id, item);
      });

      let hasActiveNonDefault = false;
      const processedData = safeAll
        .map((item: any) => {
          const mcpConnectionId = connectionMap.get(item.id)?.id;
          const isAdUpAITracking =
            connectionMap.get(item.id)?.tara_mcp?.slug === 'adup_ai_tracking';
          const isActive = connectionMap.get(item.id)?.is_active;
          const isDefault = item?.is_default;

          // Shopify's access_token and client_secret are WRITE-ONLY: never
          // surface a stored secret back into the UI/state. Leaving the field
          // blank on update omits it from the payload so the backend keeps
          // the stored value. (The client id is not a secret and stays — it
          // also tells the popup which auth mode the connection uses.)
          let savedCredentials = connectionMap.get(item.id)?.credentials;
          if (item?.slug === SHOPIFY_MCP_SLUG && savedCredentials) {
            savedCredentials = { ...savedCredentials };
            delete savedCredentials[SHOPIFY_TOKEN_FIELD];
            delete savedCredentials[SHOPIFY_CLIENT_SECRET_FIELD];
          }

          let formattedMCP = {
            id: item?.id,
            mcp_slug: item?.slug,
            mcp_name: platformDisplayName(item?.slug, item?.mcp_name, t),
            credentials_fields:
              item?.slug === 'google_ads' //? Temporary solution for Google Ads MCP account fetch issue in BE
                ? [...item?.credentials_fields, 'MANAGER_ACCOUNT_ID']
                : item?.credentials_fields,
            logo: item?.logo,
            connection_id: mcpConnectionId,
            credentials: savedCredentials,
            // Secret fields the connection already stores. Their values are
            // never returned; a blank field on update keeps them.
            configured_secret_fields: Array.isArray(
              connectionMap.get(item.id)?.configured_secret_fields
            )
              ? connectionMap.get(item.id)?.configured_secret_fields
              : [],
            is_active: isActive,
            // Raw, never defaulted: `is_valid` is NOT NULL on the credential
            // row, so a missing value means the payload did not carry one —
            // that is "unknown", not "fine". Defaulting to true here is what
            // let a rejected credential read as healthy.
            is_valid: connectionMap.get(item.id)?.is_valid,
            is_default: isDefault,
            configs: item?.configs,
            oauth_url: item?.oauth_url,
            oauth_required: connectionMap.get(item.id)?.oauth_required,
            accounts: connectionMap.get(item.id)?.accounts,
            isAdUpAITracking,
          };

          if (isDefault === false && isActive === true) {
            hasActiveNonDefault = true;
          }

          return formattedMCP;
        })
        // AI tracking is retired — never surface the AdUp AI Tracker as a
        // connectable integration (and its config route is gone).
        .filter(
          (m: any) => m.mcp_slug !== 'adup_ai_tracking' && !m.isAdUpAITracking
        );

      //* Trigger intercom event when the user connects at least 1 third party MCP
      if (INTERCOM_ENABLED_ENVS.includes(APP_ENV) && hasActiveNonDefault) {
        console.log('INTERCOM Event Triggered: mcp-connected');
        // @ts-ignore:next-line
        window.Intercom && window.Intercom('trackEvent', 'mcp-connected');
      }

      return processedData;
    }

    try {
      setIsLoading(!noLoader);
      // Await sequentially so a rejection in EITHER call is caught here. The old
      // nested .then() chain had no .catch, so any throw inside it (e.g. a
      // non-array result, or a 401) left the skeleton spinning forever. The
      // finally guarantees the loader always clears.
      const allMCPs = await get_all_available_mcp_api_call();
      const connectedMCPs = await get_all_connected_mcp_api_call();
      const data = processData(allMCPs, connectedMCPs);
      setMcpList(data);
      onListLoaded?.(data);
      if (errorMcpId) {
        const mcp = data.find((mcp: any) => mcp?.connection_id === errorMcpId);
        if (mcp) {
          if (mcp?.isAdUpAITracking) {
            navigate(
              `/tracking/integration?connection_id=${mcp?.connection_id}&is_active=${mcp?.is_active}`
            );
          } else {
            oAuthRedirectHandler(mcp);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      submitLogToBugsnag('error', `Error in fetching MCP data: ${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const getCustomServers = async () => {
    const state = store.getState();
    const activeShopSlug = state.user?.profile?.active_shop_slug;
    const hasAgencyToken = Boolean(state.auth?.agency?.accessToken);
    // No agency bridge / no active brand → nothing to query. Failures are
    // swallowed so the built-in grid never breaks because the custom probe
    // errored.
    if (!hasAgencyToken || !activeShopSlug) {
      setCustomServers([]);
      return;
    }
    try {
      const connections = await listMcpConnections(activeShopSlug);
      setCustomServers(
        (connections || []).filter((conn) => conn.type === 'custom')
      );
    } catch (error) {
      console.error('Error fetching custom MCP servers:', error);
      submitLogToBugsnag(
        'error',
        `Error fetching custom MCP servers: ${error}`
      );
      setCustomServers([]);
    }
  };

  const validateAdUpAITrackingMCP = async () => {
    const adUpAITrackingMCP: any = mcpList.find(
      (item: any) => item?.isAdUpAITracking
    );
    const isActive = adUpAITrackingMCP?.is_active;
    const mcpConnectionId = adUpAITrackingMCP?.connection_id;

    if (
      (isTrackingScriptConnectionSuccess === 'true' && !isActive) ||
      (isTrackingScriptConnectionSuccess === 'false' && isActive)
    ) {
      //* Update MCP if tracking script connection status is not the same as the MCP's is_active
      await update_mcp_api_call(
        mcpConnectionId,
        {},
        isTrackingScriptConnectionSuccess === 'true' ? true : false,
        undefined,
        false
      );

      const updatedList = mcpList.map((mcp: any) => {
        if (mcp.isAdUpAITracking) {
          return {
            ...mcp,
            is_active:
              isTrackingScriptConnectionSuccess === 'true' ? true : false,
          };
        }
        return mcp;
      });
      setMcpList(updatedList);
    }
  };

  const constructDynamicOauthURL = (
    providerOauthURL: string,
    payload: object
  ) => {
    // btoa() throws on any character outside Latin-1, and the payload can
    // carry user data like ad-account names ("Dulmi’s Ad Account"), so the
    // JSON is UTF-8-escaped first. Decoded symmetrically in oAuthSaveHandler.
    const STATE = btoa(unescape(encodeURIComponent(JSON.stringify(payload))));
    const REDIRECT_URI = `${window.location.origin}/integrations`;

    const URL = `${providerOauthURL}&redirect_uri=${REDIRECT_URI}&state=${STATE}`;
    return URL;
  };

  // Leaves the app entirely for the provider's consent screen. Only called
  // once the user has agreed to that (or from the status toggle, which is a
  // deliberate act on an existing connection).
  const performOauthRedirect = (editingMCP: any) => {
    try {
      setIsSubmitting(true);
      const redirectURL = constructDynamicOauthURL(
        editingMCP?.oauth_url,
        editingMCP
      );
      window.location.href = redirectURL; //? Redirecting to respective OAuth consent screen
    } catch (error) {
      setIsSubmitting(false);
      console.error(
        `${editingMCP?.configs?.oauth?.provider} OAuth - Error connecting MCP:`,
        error
      );
      submitLogToBugsnag(
        'error',
        `${editingMCP?.configs?.oauth?.provider} OAuth - Error in connecting MCP: ${error}`
      );
      store.dispatch(
        setMessage({
          message: `${t('MCP-OauthFailed')}`,
          messageType: 'error',
        })
      );
    }
  };

  const oAuthRedirectHandler = (
    editingMCP: any,
    isToggleClick: boolean = false,
    toggleStatus: boolean = false
  ) => {
    const mode = editingMCP?.is_active !== undefined ? 'update' : 'add';

    const isOauth =
      editingMCP?.oauth_url &&
      isNotEmptyString(editingMCP?.oauth_url) &&
      (mode === 'update' ? editingMCP?.oauth_required : true);

    if (isOauth) {
      // Clicking Connect/Configure used to navigate straight off the app to
      // the provider with no warning, losing anything in flight. Ask first —
      // except on the status toggle, which keeps its current behaviour.
      if (isToggleClick) {
        performOauthRedirect(editingMCP);
        return;
      }
      setPendingOauthMCP(editingMCP);
    } else {
      if (isToggleClick) {
        handleStatusToggle(editingMCP, toggleStatus);
      } else if (mode === 'add' && isOauthConnectUnavailable(editingMCP)) {
        // An OAuth platform whose sign-in is not set up on this environment
        // (no consent URL). Its credential form cannot work without the
        // token, so say so instead of opening it.
        store.dispatch(
          setMessage({
            message: `${t('MCP-OauthUnavailable', {
              mcpName: makeFirstLetterCapital(editingMCP?.mcp_name),
            })}`,
            messageType: 'warning',
          })
        );
      } else if (
        mode === 'add' &&
        !(editingMCP?.credentials_fields?.length > 0)
      ) {
        // KEYLESS platform (e.g. Wikipedia): there is nothing to type, so
        // "Connect" connects straight away instead of opening an empty
        // credential popup. Mirrors the OAuth-return branch in
        // oAuthSaveHandler, which already short-circuits zero-field
        // platforms. Configure (mode 'update') still opens the popup so the
        // Disconnect button stays reachable.
        onSubmitHandler(editingMCP);
      } else {
        openEditPopup(editingMCP);
      }
    }
  };

  // Turn central-api's OAuth-return params into a visible toast. `reason` slugs
  // come from IntegrationOAuthController's failure redirects; anything unknown
  // falls back to the generic OAuth-failed copy.
  const handleIntegrationReturn = (urlParams: URLSearchParams) => {
    const connected = urlParams.get('connected');
    const integrationError = urlParams.get('integration_error');
    const shopifyInstall = urlParams.get('shopify_install');
    const reason = urlParams.get('reason') ?? '';

    if (connected) {
      store.dispatch(
        setMessage({
          message: `${t('Integration-ConnectReturnSuccess')}`,
          messageType: 'success',
        })
      );
      return;
    }

    if (shopifyInstall) {
      // Store-initiated install (Shopify App Store / install link): the token
      // exchange already happened server-side; `linked` counts how many brand
      // connections were refreshed by domain (0 = store not connected to a
      // brand yet — finish from the Shopify card).
      const linked = Number(urlParams.get('linked') ?? '0');
      store.dispatch(
        setMessage({
          message:
            linked > 0
              ? `${t('Shopify-StoreInstallLinked')}`
              : `${t('Shopify-StoreInstallUnlinked')}`,
          messageType: linked > 0 ? 'success' : 'warning',
        })
      );
      return;
    }

    if (integrationError) {
      let messageKey = 'MCP-OauthFailed';
      let messageType: 'success' | 'warning' | 'error' = 'error';

      if (reason === 'already_completed') {
        // The browser re-requested the callback (back button / prefetch)
        // after the flow had already connected — the single-use state was
        // burned on the first, successful hit. Nothing is wrong; the grid
        // refresh below shows the connection.
        messageKey = 'Integration-AlreadyCompleted';
        messageType = 'success';
      } else if (reason === 'denied') {
        messageKey = 'Integration-ConnectDenied';
        messageType = 'warning';
      } else if (reason.startsWith('validation_failed')) {
        messageKey = 'Integration-ConnectValidationFailed';
      } else if (reason === 'oauth_not_configured') {
        messageKey = 'Integration-OauthNotConfigured';
      } else if (
        ['hmac_rejected', 'state_rejected', 'shop_mismatch', 'missing_code_or_state'].includes(
          reason
        )
      ) {
        messageKey = 'Integration-ConnectReturnNotVerified';
      }

      store.dispatch(
        setMessage({ message: `${t(messageKey)}`, messageType })
      );
    }
  };

  const decodeOauthState = (state: string) => {
    const raw = atob(state);
    try {
      // Reverse of constructDynamicOauthURL's UTF-8-safe encoding.
      return JSON.parse(decodeURIComponent(escape(raw)));
    } catch {
      // A consent flow started before this encoding shipped round-trips a
      // plain-btoa state; fall back to parsing it as-is.
      return JSON.parse(raw);
    }
  };

  const oAuthSaveHandler = async (code: string, state: string) => {
    try {
      const decodedState = decodeOauthState(state);
      const mcp = { ...decodedState, oauthCode: code };

      const response = await save_mcp_oauth_api_call(mcp.id, mcp.oauthCode);

      await getMCPs();

      if (response?.success) {
        if (mcp?.credentials_fields?.length > 0) {
          openEditPopup({
            ...mcp,
            accounts: response?.data?.accounts,
          });
        } else {
          onSubmitHandler(mcp);
        }
      } else {
        openEditPopup(mcp);
        store.dispatch(
          setMessage({
            message: isNotEmptyString(response?.message)
              ? response?.message
              : `${t('MCP-OauthFailed')}`,
            messageType: isNotEmptyString(response?.message)
              ? 'warning'
              : 'error',
          })
        );
      }
    } catch (error) {
      console.error(`OAuth - Failed to decode state:`, error);
    }
  };

  const onSubmitHandler = async (editingMCP: any) => {
    if (!canManage) return;

    // Shopify: normalize the store domain ("acme" / pasted URL →
    // "acme.myshopify.com") and validate the active auth mode's fields before
    // hitting the credentials endpoint. Secrets (access token / client
    // secret) are write-only: blank on update means "keep the stored value",
    // so they are omitted from the payload. The inactive mode's fields are
    // stripped — the backend treats the two shapes as mutually exclusive and
    // uses the provided shape to switch modes.
    if (editingMCP?.mcp_slug === SHOPIFY_MCP_SLUG) {
      const credentials: any = { ...(editingMCP?.credentials || {}) };

      const normalizedDomain = normalizeShopifyDomain(
        credentials[SHOPIFY_DOMAIN_FIELD] || ''
      );
      if (!isValidShopifyDomain(normalizedDomain)) {
        store.dispatch(
          setMessage({
            message: `${t('Shopify-DomainValidation')}`,
            messageType: 'warning',
          })
        );
        return;
      }
      credentials[SHOPIFY_DOMAIN_FIELD] = normalizedDomain;

      const isExistingConnection = editingMCP?.is_active !== undefined;

      // OAuth install: there is nothing to save here. Hand off to Shopify —
      // the merchant approves on their own store and our public callback
      // exchanges the code for the offline token and writes the credential
      // row server-side. The store domain is the only input, and it is bound
      // into the signed OAuth state so the callback can prove the store that
      // comes back is the one we sent the merchant to.
      if (shopifyAuthMode === 'oauth') {
        // The connect-url route is employee-auth + shop-scoped, so it needs the
        // active brand slug (this handler has no shop in scope otherwise).
        const activeShopSlug =
          store.getState().user?.profile?.active_shop_slug;

        if (!isNotEmptyString(activeShopSlug)) {
          store.dispatch(
            setMessage({
              message: `${t('Shopify-ConnectFailed')}`,
              messageType: 'error',
            })
          );
          return;
        }

        // Open the Shopify tab NOW, synchronously inside the click gesture, so
        // the browser's popup blocker allows it — we only have the signed URL
        // after the await below, which would break the user-gesture chain. The
        // tab starts blank and we point it at the connect URL once it arrives.
        // A new tab (not a same-tab redirect) is deliberate: Shopify's
        // authorize screen refuses to render in an iframe, and this keeps our
        // app open in the original tab while the merchant approves the install.
        const shopifyTab = window.open('about:blank', '_blank');
        if (shopifyTab) {
          // Sever the opener link so the Shopify-controlled tab cannot reach
          // back into our window via window.opener.
          try {
            shopifyTab.opener = null;
          } catch {
            /* some browsers disallow reassigning opener — safe to ignore */
          }
        }

        setIsSubmitting(true);
        try {
          const { url } = await getConnectUrl(
            activeShopSlug,
            SHOPIFY_MCP_SLUG,
            undefined,
            normalizedDomain
          );

          if (!isNotEmptyString(url)) {
            throw new Error('No connect URL returned');
          }

          // The public callback exchanges the code and lands on /integrations
          // inside this new tab. If the popup was blocked, fall back to a
          // same-tab redirect so the merchant can still complete the install.
          if (shopifyTab) {
            shopifyTab.location.href = url;
          } else {
            window.location.href = url;
          }
          setIsSubmitting(false);
          return;
        } catch (error: any) {
          if (shopifyTab) {
            shopifyTab.close();
          }
          setIsSubmitting(false);
          // Surface the backend's specific reason instead of a generic
          // "check the store domain" — the real cause is usually server-side
          // (e.g. `oauth_not_configured` when SHOPIFY_APP_CLIENT_ID /
          // SHOPIFY_APP_CLIENT_SECRET are unset), which the generic copy hides
          // and sends the merchant chasing a domain that is actually fine.
          store.dispatch(
            setMessage({
              message: isNotEmptyString(error?.message)
                ? error.message
                : `${t('Shopify-ConnectFailed')}`,
              messageType: 'error',
            })
          );
          return;
        }
      }

      // Bring-your-own-app: the agency's OWN custom-distribution app for this
      // store. Same hand-off shape as `oauth`, but the authorize URL is built
      // with the BRAND's client pair (posted here, stored encrypted
      // server-side before the redirect). Works pre-review for any store.
      if (shopifyAuthMode === 'agency_app') {
        const activeShopSlug =
          store.getState().user?.profile?.active_shop_slug;

        if (!isNotEmptyString(activeShopSlug)) {
          store.dispatch(
            setMessage({
              message: `${t('Shopify-ConnectFailed')}`,
              messageType: 'error',
            })
          );
          return;
        }

        const clientId =
          typeof credentials[SHOPIFY_CLIENT_ID_FIELD] === 'string'
            ? credentials[SHOPIFY_CLIENT_ID_FIELD].trim()
            : '';
        const clientSecret =
          typeof credentials[SHOPIFY_CLIENT_SECRET_FIELD] === 'string'
            ? credentials[SHOPIFY_CLIENT_SECRET_FIELD].trim()
            : '';

        // Blank pair on an EXISTING connection = reconnect with the stored
        // pair (the backend reuses it). A new connection must provide both.
        if (clientId !== '' && !isValidShopifyClientId(clientId)) {
          store.dispatch(
            setMessage({
              message: `${t('Shopify-ClientIdValidation')}`,
              messageType: 'warning',
            })
          );
          return;
        }
        if (clientSecret !== '' && !isValidShopifyClientSecret(clientSecret)) {
          store.dispatch(
            setMessage({
              message: `${t('Shopify-ClientSecretValidation')}`,
              messageType: 'warning',
            })
          );
          return;
        }
        if ((clientId === '' || clientSecret === '') && !isExistingConnection) {
          store.dispatch(
            setMessage({
              message: `${t('Shopify-AgencyAppPairRequired')}`,
              messageType: 'warning',
            })
          );
          return;
        }

        // Same popup-blocker-safe new-tab pattern as the oauth branch.
        const shopifyTab = window.open('about:blank', '_blank');
        if (shopifyTab) {
          try {
            shopifyTab.opener = null;
          } catch {
            /* some browsers disallow reassigning opener — safe to ignore */
          }
        }

        setIsSubmitting(true);
        try {
          const { url } = await getAgencyAppConnectUrl(
            activeShopSlug,
            normalizedDomain,
            clientId || undefined,
            clientSecret || undefined
          );

          if (!isNotEmptyString(url)) {
            throw new Error('No connect URL returned');
          }

          if (shopifyTab) {
            shopifyTab.location.href = url;
          } else {
            window.location.href = url;
          }
          setIsSubmitting(false);
          return;
        } catch (error: any) {
          if (shopifyTab) {
            shopifyTab.close();
          }
          setIsSubmitting(false);
          store.dispatch(
            setMessage({
              message: isNotEmptyString(error?.message)
                ? error.message
                : `${t('Shopify-ConnectFailed')}`,
              messageType: 'error',
            })
          );
          return;
        }
      }

      if (shopifyAuthMode === 'client_credentials') {
        delete credentials[SHOPIFY_TOKEN_FIELD];

        const clientId =
          typeof credentials[SHOPIFY_CLIENT_ID_FIELD] === 'string'
            ? credentials[SHOPIFY_CLIENT_ID_FIELD].trim()
            : '';
        if (!isValidShopifyClientId(clientId)) {
          store.dispatch(
            setMessage({
              message: `${t('Shopify-ClientIdValidation')}`,
              messageType: 'warning',
            })
          );
          return;
        }
        credentials[SHOPIFY_CLIENT_ID_FIELD] = clientId;

        const clientSecret =
          typeof credentials[SHOPIFY_CLIENT_SECRET_FIELD] === 'string'
            ? credentials[SHOPIFY_CLIENT_SECRET_FIELD].trim()
            : '';
        if (clientSecret === '' && isExistingConnection) {
          delete credentials[SHOPIFY_CLIENT_SECRET_FIELD];
        } else if (!isValidShopifyClientSecret(clientSecret)) {
          store.dispatch(
            setMessage({
              message: `${t('Shopify-ClientSecretValidation')}`,
              messageType: 'warning',
            })
          );
          return;
        } else {
          credentials[SHOPIFY_CLIENT_SECRET_FIELD] = clientSecret;
        }
      } else {
        delete credentials[SHOPIFY_CLIENT_ID_FIELD];
        delete credentials[SHOPIFY_CLIENT_SECRET_FIELD];

        const token =
          typeof credentials[SHOPIFY_TOKEN_FIELD] === 'string'
            ? credentials[SHOPIFY_TOKEN_FIELD].trim()
            : '';
        if (token === '' && isExistingConnection) {
          delete credentials[SHOPIFY_TOKEN_FIELD];
        } else if (!isValidShopifyToken(token)) {
          store.dispatch(
            setMessage({
              message: `${t('Shopify-TokenValidation')}`,
              messageType: 'warning',
            })
          );
          return;
        } else {
          credentials[SHOPIFY_TOKEN_FIELD] = token;
        }
      }

      editingMCP = { ...editingMCP, credentials };
      setEditingMCP(editingMCP);
    }

    const mode = editingMCP?.is_active !== undefined ? 'update' : 'add';
    if (mode === 'add') {
      try {
        setIsSubmitting(true);
        setModalErrorMessage(undefined);
        const response = await connect_mcp_api_call(
          editingMCP.id,
          editingMCP.credentials
        );
        if (response?.success) {
          setIsSubmitting(false);
          // Only an explicit is_valid false is a rejection; a response
          // without is_valid closes the popup and the reload shows the state.
          if (!isMcpSaveRejected(response?.data)) {
            getMCPs(false);
            toggleEditPopupModal(false);
            if (editingMCP?.isAIOptimizeRedirection) {
              navigate('/ai-optimizations/automated?integrationsRedirect=true');
            }
          } else {
            getMCPs(true);
            credentialsBaselineRef.current = response?.data?.credentials;
            setEditingMCP({
              ...editingMCP,
              connection_id: response?.data?.id,
              credentials: response?.data?.credentials,
              is_active: true,
              is_valid: false,
            });
            store.dispatch(
              setMessage({
                message: `${t('MCP-InvalidCredentials')}`,
                messageType: 'warning',
              })
            );
          }
        } else {
          setIsSubmitting(false);
          reportSaveFailure(response);
        }
      } catch (error) {
        setIsSubmitting(false);
        console.error('Error connecting MCP:', error);
        submitLogToBugsnag('error', `Error in connecting MCP: ${error}`);
        reportSaveFailure(undefined);
      }
    } else {
      try {
        setIsSubmitting(true);
        setModalErrorMessage(undefined);
        // Saving from the popup turns a switched-off connection back on;
        // built-in servers keep their state.
        const isActiveAfterUpdate = editingMCP?.is_default
          ? editingMCP.is_active
          : true;
        // Only send credentials the user actually changed; otherwise the
        // request carries is_active alone and the stored ones are kept.
        const response = await update_mcp_api_call(
          editingMCP.connection_id,
          hasMcpCredentialChanges(
            editingMCP.credentials,
            credentialsBaselineRef.current
          )
            ? withoutBlankSavedSecrets(editingMCP.credentials, editingMCP)
            : undefined,
          isActiveAfterUpdate
        );
        if (response?.success) {
          setIsSubmitting(false);
          // Only an explicit rejection keeps the popup open; a response
          // without is_valid closes it and the reload shows the real state.
          if (
            !isMcpSaveRejected(response?.data) ||
            !isActiveAfterUpdate ||
            editingMCP?.is_default
          ) {
            getMCPs(false);
            toggleEditPopupModal(false);
            if (editingMCP?.isAIOptimizeRedirection) {
              navigate('/ai-optimizations/automated?integrationsRedirect=true');
            }
          } else {
            getMCPs(true);
            setEditingMCP({
              ...editingMCP,
              is_active: isActiveAfterUpdate,
              is_valid: false,
            });
            setIsActivationToggleConfirmationVisible(false);
            store.dispatch(
              setMessage({
                message: `${t('MCP-InvalidCredentials')}`,
                messageType: 'warning',
              })
            );
          }
        } else {
          setIsSubmitting(false);
          reportSaveFailure(response);
        }
      } catch (error) {
        setIsSubmitting(false);
        console.error('Error updating MCP:', error);
        submitLogToBugsnag('error', `Error in updating MCP: ${error}`);
        reportSaveFailure(undefined);
      }
    }
  };

  const isLastActiveMCP = () => {
    const activeMCPs = mcpList.filter(
      (mcp) => mcp.is_active === true && !mcp.is_default
    );
    return activeMCPs.length === 1 && activeMCPs[0].id === editingMCP?.id;
  };

  const handleDisconnectionReasonSubmit = async (reason: string) => {
    try {
      setIsSubmittingReason(true);
      await why_are_you_disconnecting_api_call(reason);
      await performDelete();
      setIsDisconnectionReasonPopupOpen(false);
      setIsSubmittingReason(false);
    } catch (error) {
      setIsSubmittingReason(false);
      console.error('Error submitting disconnection reason:', error);
      submitLogToBugsnag(
        'error',
        `Error in submitting disconnection reason: ${error}`
      );
      store.dispatch(
        setMessage({
          message: `${t('SomethingWentWrong-trying-again-msg')}`,
          messageType: 'error',
        })
      );
    }
  };

  const performDelete = async () => {
    try {
      const mcpToDelete = preservedEditingMCP || editingMCP;
      const response = await delete_mcp_api_call(mcpToDelete.connection_id);
      if (response) {
        getMCPs();
        setEditingMCP(undefined);
        setPreservedEditingMCP(undefined);
        setManualAccountFields(new Set());
        setIsEditPopupOpen(false);
        setIsActivationToggleConfirmationVisible(false);
        setIsSubmitting(false);
        setIsDeleting(false);
      } else {
        throw new Error('Delete API failed');
      }
    } catch (error) {
      console.error('Error deleting MCP:', error);
      submitLogToBugsnag('error', `Error in deleting MCP: ${error}`);
      throw error;
    }
  };

  const performDeleteWithLoading = async () => {
    setIsDeleting(true);
    try {
      await performDelete();
    } catch (error) {
      setIsDeleting(false);
      throw error;
    }
  };

  const deleteHandler = async (e: any) => {
    e.preventDefault();
    if (!canManage) return;

    if (isLastActiveMCP()) {
      setPreservedEditingMCP(editingMCP);
      setIsEditPopupOpen(false);
      setIsActivationToggleConfirmationVisible(false);
      setIsSubmitting(false);
      setIsDeleting(false);
      setIsDisconnectionReasonPopupOpen(true);
    } else {
      await performDeleteWithLoading();
    }
  };

  const handleGoBack = () => {
    setIsDisconnectionReasonPopupOpen(false);
    setEditingMCP(preservedEditingMCP);
    setIsEditPopupOpen(true);
  };

  const handleStatusToggle = async (mcp: any, newStatus: boolean) => {
    if (!canManage) return;
    if (
      mcp?.isAdUpAITracking &&
      isTrackingScriptConnectionSuccess !== 'true' &&
      newStatus
    ) {
      store.dispatch(
        setMessage({
          message: `${t('IntegrationPending-2')}`,
          messageType: 'warning',
        })
      );
      return;
    }

    try {
      setIsSubmitting(true);
      // The switch changes is_active only; leaving credentials out keeps
      // the stored ones instead of overwriting them with the list's copy.
      const response = await update_mcp_api_call(
        mcp.connection_id,
        undefined,
        newStatus,
        undefined,
        false
      );

      if (response?.success) {
        //? Update the mcpList state directly
        setMcpList((prevList) =>
          prevList.map((item) => {
            if (item.id === mcp.id) {
              return {
                ...item,
                is_active: newStatus,
                is_valid: response?.data?.is_valid ?? item.is_valid,
              };
            }
            return item;
          })
        );
      }
    } catch (error) {
      console.error('Error updating MCP status:', error);
      submitLogToBugsnag('error', `Error in updating MCP status: ${error}`);
      store.dispatch(
        setMessage({
          message: `${t('SomethingWentWrong-trying-again-msg')}`,
          messageType: 'error',
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const searchMCPs = (searchValue: string, mcpList: any[]) => {
    if (isNotEmptyString(searchValue)) {
      const searchLower = searchValue.toLowerCase();
      const startsWithMatches = mcpList.filter((mcp) =>
        mcp.mcp_name.toLowerCase().startsWith(searchLower)
      );
      const includesMatches = mcpList.filter(
        (mcp) =>
          mcp.mcp_name.toLowerCase().includes(searchLower) &&
          !mcp.mcp_name.toLowerCase().startsWith(searchLower)
      );
      return [...startsWithMatches, ...includesMatches];
    }
    return mcpList;
  };

  const handleRefreshAccounts = async (field: string) => {
    if (!editingMCP?.connection_id) return;

    try {
      setIsRefreshingAccounts(true);
      const response = await refresh_mcp_accounts_api_call(
        editingMCP.connection_id
      );

      if (response && response.accounts) {
        setEditingMCP({
          ...editingMCP,
          accounts: {
            ...editingMCP.accounts,
            [field]: response.accounts[field] || editingMCP.accounts[field],
          },
        });

        store.dispatch(
          setMessage({
            message: `${t('MCP-AccountsRefreshed')}`,
            messageType: 'success',
          })
        );
      } else {
        store.dispatch(
          setMessage({
            message: `${t('MCP-AccountsRefreshFailed')}`,
            messageType: 'error',
          })
        );
      }
    } catch (error) {
      console.error('Error refreshing accounts:', error);
      store.dispatch(
        setMessage({
          message: `${t('MCP-AccountsRefreshFailed')}`,
          messageType: 'error',
        })
      );
    } finally {
      setIsRefreshingAccounts(false);
    }
  };

  // Custom servers honour the same search box; for the status filter they are
  // always "connected" entities, so only the relevant statuses surface them
  // (an OAuth server that still needs its sign-in files under Finish Setup).
  const filteredCustomServers = customServers
    .filter((conn) => {
      if (!isNotEmptyString(searchValue)) return true;
      return conn.name?.toLowerCase().includes(searchValue.toLowerCase());
    })
    .filter((conn) => matchesCustomServerFilter(conn, selectedFilter));

  // The Configure popup's warning tag. Same decision as the card pill, so the
  // popup can never accuse a platform of bad credentials while the pill behind
  // it reads "Status Unknown".
  const editingCredentialNoticeKey = getMcpCredentialNoticeKey(editingMCP);

  return (
    <>
      <div className='taraSearch'>
        <div>
          <div className='integrationContainer'>
            {isLoading ? (
              <LoadingSpinner />
            ) : (
              <>
                {filteredMcpList?.length > 0 &&
                  filteredMcpList.map((mcp) => {
                    // Embed the Meta System-User connect CTA directly on the
                    // Facebook card (Meta-only — a non-expiring token with
                    // better rate limits than the OAuth user token).
                    const isMeta =
                      /facebook/i.test(mcp?.mcp_slug || '') ||
                      /facebook|meta/i.test(mcp?.mcp_name || '');
                    // The pill tells the truth about the credential; `active`
                    // still only drives the affordance (Configure vs Connect)
                    // and the on/off toggle.
                    const statusView = getMcpConnectionStatusView(mcp);
                    return (
                      <MCPStatusCard
                        key={mcp.id}
                        customPillLabel={t(statusView.labelKey)}
                        customPillColor={statusView.color}
                        extraContent={
                          isMeta && SHOW_META_SYSTEM_USER_CONNECT ? (
                            <MetaSystemUserSection compact />
                          ) : isStripeReportingOnly(mcp?.mcp_slug) ? (
                            <p className='mcpStatusCard__hint'>
                              {t('Stripe-ReportingOnlyHint')}
                            </p>
                          ) : undefined
                        }
                        hasOAuth={isNotEmptyString(mcp?.oauth_url)}
                        info={makeFirstLetterCapital(mcp.mcp_name)}
                        active={mcp.is_active?.toString() ?? 'null'}
                        image={isNotEmptyString(mcp?.logo) ? mcp?.logo : mcpImg}
                        onClick={() => {
                          if (!canManage) return;
                          if (mcp?.isAdUpAITracking) {
                            navigate(
                              `/tracking/integration?connection_id=${mcp?.connection_id}&is_active=${mcp?.is_active}`
                            );
                          } else {
                            oAuthRedirectHandler(mcp);
                          }
                        }}
                        disabled={
                          !canManage || isSubmitting || isDeleting || isLoading
                        }
                        // A dead credential (reconnect_needed) must not be
                        // switched on or off, but the Configure/reconnect path
                        // stays open — so this gates only the toggle, never the
                        // affordance above.
                        toggleDisabled={
                          statusView.status === 'reconnect_needed'
                        }
                        hideConfigure={
                          // No Connect / Configure for a member who may not
                          // manage integrations: the card is view-only.
                          !canManage ||
                          (mcp?.is_default && !mcp?.isAdUpAITracking)
                        }
                        iconTitle={
                          statusView.status === 'reconnect_needed'
                            ? t('MCP-InvalidCredentials')
                            : statusView.status === 'action_needed'
                            ? t('MCP-OauthConsentRequired')
                            : statusView.status === 'unknown'
                            ? t('MCP-StatusUnknown')
                            : ''
                        }
                        icon={
                          // Secondary to the pill now, and driven by the same
                          // decision so the tooltip can never contradict it.
                          statusView.status === 'reconnect_needed' ||
                          statusView.status === 'action_needed' ||
                          statusView.status === 'unknown'
                            ? warningSVG
                            : undefined
                        }
                        onStatusToggle={(newStatus) =>
                          canManage &&
                          oAuthRedirectHandler(mcp, true, newStatus)
                        }
                      />
                    );
                  })}
                {/* Custom (Control Center) servers — read-only here; managed
                    on the Governance tab. Distinguished by the "Custom" pill,
                    with no connect/configure/toggle.

                    The logo is the server's OWN host favicon: there is no logo
                    field on an mcp_connection, and rendering the same generic
                    plug for every custom server made DeepWiki and a throwaway
                    test connection visually identical. Vendor-agnostic, so any
                    custom MCP gets its real mark with no code change. A missing
                    or failing favicon falls back to the generic mark via the
                    card's existing onError handler. */}
                {filteredCustomServers.map((conn) => {
                  // An OAuth server whose sign-in is missing or dead says so
                  // (fixed by Connect / Reconnect on the MCP Servers tab);
                  // every other custom server keeps the "Custom" pill.
                  const oauthPill = getCustomOauthPillView(conn);
                  const isBroken = !!oauthPill || conn.status === 'error';
                  return (
                    <MCPStatusCard
                      key={`custom-${conn.id}`}
                      info={conn.name}
                      image={customMcpLogoUrl(conn.url) ?? mcpImg}
                      active={isBroken ? 'false' : 'true'}
                      customPillLabel={
                        oauthPill ? t(oauthPill.labelKey) : t('Custom')
                      }
                      customPillColor={
                        oauthPill
                          ? oauthPill.color
                          : conn.status === 'error'
                          ? 'red'
                          : 'green'
                      }
                      icon={isBroken ? warningSVG : undefined}
                      iconTitle={
                        oauthPill?.status === 'reconnect_needed'
                          ? t('ControlCenterOauthReconnectHint')
                          : oauthPill
                          ? t('ControlCenterOauthAuthorizeHint')
                          : conn.status === 'error'
                          ? conn.last_error || t('MCP-InvalidCredentials')
                          : ''
                      }
                      hideConfigure
                      isToggleHidden
                      onCardClick={() =>
                        navigate('/integrations?tab=governance')
                      }
                    />
                  );
                })}
                {filteredMcpList.length === 0 &&
                  filteredCustomServers.length === 0 && (
                    <div
                      style={{
                        width: '100%',
                      }}
                    >
                      <NoData
                        title={
                          isNotEmptyString(searchValue)
                            ? t('NoMCP-Search', {
                                mcpName: searchValue,
                              })
                            : t('NoMCP')
                        }
                        subTitle={
                          !isNotEmptyString(searchValue)
                            ? t('ContactSupport')
                            : undefined
                        }
                      />
                    </div>
                  )}
              </>
            )}
          </div>
        </div>
      </div>
      <Popup
        styles={{ backgroundColor: '#fff' }}
        setTrigger={() => toggleEditPopupModal(false)}
        trigger={isEditPopupOpen}
      >
        <form
          id='mcpModal-form-update'
          onSubmit={(e) => {
            e.preventDefault();
            onSubmitHandler(editingMCP);
          }}
          className='mcpConfigBox'
        >
          <div className='mcpConfigBox__title'>
            <div className='mcpConfigBox__title--main'>
              <div className='mcpConfigBox__title--main-image'>
                <img
                  src={editingMCP?.logo}
                  alt={makeFirstLetterCapital(editingMCP?.mcp_name)}
                  onError={swapToBundledMcpLogo}
                />
              </div>
              <h1 className='mcpConfigBox__title--main-text'>
                {t('ManageMCP', {
                  mcpName: makeFirstLetterCapital(editingMCP?.mcp_name),
                })}
              </h1>
            </div>

            <div className='mcpConfigBox__title--tags'>
              {isStripeReportingOnly(editingMCP?.mcp_slug) && (
                <p className='mcpStatusCard__hint'>
                  {t('Stripe-ReportingOnlyHint')}
                </p>
              )}
              {/* Built-ins and rows that were never connected resolve to no
                  key at all, so this tag no longer fires on them either. */}
              {editingCredentialNoticeKey && (
                <div
                  className='collapseList__item--top-left-tag collapseList__item--top-left-tag-yellow'
                  style={{ width: 'fit-content', marginTop: '0.5rem' }}
                >
                  ⚠️ {t(editingCredentialNoticeKey)}
                </div>
              )}

              {/* Left on the raw flags on purpose: the helper's
                  `action_needed` covers only a live, valid connection, while
                  this tag must also prompt for consent on a row parked
                  mid-setup or on a rejected credential that can be fixed by
                  re-authorising. */}
              {!editingMCP?.is_default &&
                editingMCP?.oauth_required &&
                isNotEmptyString(editingMCP?.oauth_url) && (
                  <div
                    className='collapseList__item--top-left-tag collapseList__item--top-left-tag-yellow'
                    style={{ width: 'fit-content', marginTop: '0.5rem' }}
                  >
                    ⚠️ {t('MCP-OauthConsentRequired')}
                  </div>
                )}
            </div>
          </div>
          <div className='mcpConfigBox__content'>
            {/* Short collapsible setup guide: how to create the Shopify
                custom app / Dev Dashboard app and copy its credentials. */}
            {editingMCP?.mcp_slug === SHOPIFY_MCP_SLUG && <ShopifySetupGuide />}
            {/* Connect modes, own app first: `agency_app` (the agency's OWN app
                for this store — client pair fields, works pre-review) and
                `oauth` (ADUP's Shopify app, domain-only, awaiting Shopify's
                approval). Inline styles on purpose: the SCSS bundle is global
                and generic class names collide across pages. */}
            {editingMCP?.mcp_slug === SHOPIFY_MCP_SLUG && (
              <div style={{ margin: '0.6rem 0 0.2rem' }}>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {SHOPIFY_AUTH_MODE_OPTIONS.map(([mode, labelKey]) => (
                    <button
                      key={mode}
                      type='button'
                      data-testid={`shopify-auth-mode-${mode}`}
                      aria-pressed={shopifyAuthMode === mode}
                      onClick={() => setShopifyAuthMode(mode)}
                      style={{
                        padding: '0.35rem 0.8rem',
                        borderRadius: '0.5rem',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        border:
                          shopifyAuthMode === mode
                            ? '2px solid var(--main-brand-color, #6a5acd)'
                            : '1px solid rgba(128,128,128,0.4)',
                        fontWeight: shopifyAuthMode === mode ? 600 : 400,
                        background: 'transparent',
                        color: 'inherit',
                      }}
                    >
                      {t(labelKey)}
                    </button>
                  ))}
                </div>
                {shopifyAuthMode === 'agency_app' && (
                  <div
                    style={{
                      fontSize: '0.8rem',
                      opacity: 0.85,
                      marginTop: '0.45rem',
                      lineHeight: 1.45,
                      wordBreak: 'break-all',
                    }}
                  >
                    {t('Shopify-AgencyAppHint')}
                    {/* Shopify enforces App-URL-host === redirect-host, so the
                        agency must set BOTH in their app: the App URL (origin)
                        AND the exact redirect URL. Showing only the redirect
                        (the old hint) left every agency stuck on
                        `invalid_request: … matching hosts`. */}
                    {(
                      [
                        [
                          t('Shopify-AgencyAppUrlLabel'),
                          SHOPIFY_AGENCY_APP_URL_ORIGIN,
                          'Shopify-AppUrlCopied',
                        ],
                        [
                          t('Shopify-AgencyAppRedirectLabel'),
                          SHOPIFY_AGENCY_APP_CALLBACK_URL,
                          'Shopify-CallbackUrlCopied',
                        ],
                      ] as [string, string, string][]
                    ).map(([label, value, copiedKey]) => (
                      <div key={value} style={{ marginTop: '0.5rem' }}>
                        <div style={{ fontWeight: 600 }}>{label}</div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.4rem',
                            marginTop: '0.2rem',
                          }}
                        >
                          <code>{value}</code>
                          <button
                            type='button'
                            onClick={() => {
                              navigator.clipboard
                                ?.writeText(value)
                                .then(() => {
                                  store.dispatch(
                                    setMessage({
                                      message: `${t(copiedKey)}`,
                                      messageType: 'success',
                                    })
                                  );
                                })
                                .catch(() => {
                                  /* clipboard unavailable — URL is selectable */
                                });
                            }}
                            style={{
                              padding: '0.2rem 0.6rem',
                              borderRadius: '0.4rem',
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                              border: '1px solid rgba(128,128,128,0.4)',
                              background: 'transparent',
                              color: 'inherit',
                              flexShrink: 0,
                            }}
                          >
                            {t('Copy')}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className='mcpConfigBox__content--inputs'>
              {(editingMCP?.mcp_slug === SHOPIFY_MCP_SLUG
                ? getShopifyCredentialFields(shopifyAuthMode)
                : editingMCP?.credentials_fields
              )?.map(
                (field: string, index: number) => (
                  <div
                    className='mcpConfigBox__content--inputs-field'
                    key={index}
                  >
                    {editingMCP?.accounts &&
                    Array.isArray(editingMCP?.accounts?.[field]) &&
                    editingMCP?.accounts?.[field]?.length > 0 ? (
                      <>
                        {/* <AISelectInput
                          defaultValue={
                            editingMCP?.credentials?.[field] ||
                            editingMCP?.accounts?.[field]?.[0]?.OptionValue ||
                            ''
                          }
                          setSelect={(value: string) => {
                            if (value === 'manual_account') {
                              setManualAccountFields(
                                (prev) =>
                                  new Set(Array.from(prev).concat(field))
                              );
                            } else {
                              setManualAccountFields((prev) => {
                                const newSet = new Set(prev);
                                newSet.delete(field);
                                return newSet;
                              });
                            }
                            setEditingMCP({
                              ...editingMCP,
                              credentials: {
                                ...editingMCP?.credentials,
                                [field]:
                                  field === 'MYSHOPIFY_DOMAIN'
                                    ? removeHttpPrefix(value)
                                    : value,
                              },
                            });
                          }}
                          selectOptionArr={[
                            ...editingMCP?.accounts?.[field].map(
                              (value: any) => ({
                                OptionValue: value?.OptionValue,
                                OptionName: `${value?.OptionName} ${
                                  value?.OptionValue !== value?.OptionName
                                    ? `(${value?.OptionValue})`
                                    : ''
                                }`,
                              })
                            ),
                            {
                              OptionValue: 'manual_account',
                              OptionName: t('MCP-ManualAccount', {
                                credentialFieldName:
                                  convertSnakeCaseToTitleCase(field),
                              }),
                            },
                          ]}
                          placeholder={convertSnakeCaseToTitleCase(field)}
                          id={`${field}-input`}
                          refreshButton={{
                            onClick: () => handleRefreshAccounts(field),
                            isLoading: isRefreshingAccounts,
                            icon: reloadArrowsSVG,
                          }}
                        /> */}
                        <SearchSelectInput
                          defaultValue={
                            editingMCP?.credentials?.[field] ||
                            editingMCP?.accounts?.[field]?.[0]?.OptionValue ||
                            ''
                          }
                          setSelect={(value: string) => {
                            if (value === 'manual_account') {
                              setManualAccountFields(
                                (prev) =>
                                  new Set(Array.from(prev).concat(field))
                              );
                            } else {
                              setManualAccountFields((prev) => {
                                const s = new Set(prev);
                                s.delete(field);
                                return s;
                              });
                            }
                            setEditingMCP({
                              ...editingMCP,
                              credentials: {
                                ...editingMCP?.credentials,
                                [field]:
                                  field === 'MYSHOPIFY_DOMAIN'
                                    ? removeHttpPrefix(value)
                                    : value,
                              },
                            });
                          }}
                          selectOptionArr={[
                            ...editingMCP?.accounts?.[field].map(
                              (value: any) => ({
                                OptionValue: value?.OptionValue,
                                OptionName: `${value?.OptionName} ${
                                  value?.OptionValue !== value?.OptionName
                                    ? `(${value?.OptionValue})`
                                    : ''
                                }`,
                              })
                            ),
                            {
                              OptionValue: 'manual_account',
                              OptionName: t('MCP-ManualAccount', {
                                credentialFieldName:
                                  convertSnakeCaseToTitleCase(field),
                              }),
                            },
                          ]}
                          placeholder={convertSnakeCaseToTitleCase(field)}
                          id={`${field}-input`}
                          required={
                            !isOptionalMcpField(editingMCP?.mcp_slug, field) &&
                            !isSavedMcpSecret(field, editingMCP)
                          }
                          refreshButton={{
                            onClick: () => handleRefreshAccounts(field),
                            isLoading: isRefreshingAccounts,
                            icon: reloadArrowsSVG,
                          }}
                        />
                        {manualAccountFields.has(field) && (
                          <AINormalInput
                            key={`${field}-manual`}
                            changeListeners={[
                              (event) => {
                                setEditingMCP({
                                  ...editingMCP,
                                  credentials: {
                                    ...editingMCP?.credentials,
                                    [`${field}_manual`]:
                                      field === 'MYSHOPIFY_DOMAIN'
                                        ? removeHttpPrefix(event.target.value)
                                        : event.target.value,
                                  },
                                });
                              },
                            ]}
                            id={`${field}-manual-input`}
                            value={
                              editingMCP?.credentials?.[`${field}_manual`] || ''
                            }
                            required={true}
                            innerPlaceholder={
                              t(`EnterYour`) +
                              ' ' +
                              convertSnakeCaseToTitleCase(field) +
                              (field === 'MYSHOPIFY_DOMAIN'
                                ? ' ("example.myshopify.com")'
                                : '')
                            }
                            pattern={
                              (field === 'MANAGER_ACCOUNT_ID' ||
                                field === 'ACCOUNT_ID') &&
                              editingMCP?.mcp_slug === 'google_ads'
                                ? '^[0-9]+$'
                                : undefined
                            }
                            title={
                              (field === 'MANAGER_ACCOUNT_ID' ||
                                field === 'ACCOUNT_ID') &&
                              editingMCP?.mcp_slug === 'google_ads'
                                ? t('OnlyNumeric-validation')
                                : undefined
                            }
                          />
                        )}
                      </>
                    ) : field === 'MANAGER_ACCOUNT_ID' &&
                      editingMCP?.mcp_slug === 'google_ads' &&
                      editingMCP?.accounts &&
                      Array.isArray(editingMCP?.accounts?.['ACCOUNT_ID']) &&
                      editingMCP?.accounts?.['ACCOUNT_ID']?.length > 0 &&
                      editingMCP?.credentials?.['ACCOUNT_ID'] !==
                        'manual_account' ? null : (
                      <AINormalInput
                        key={index}
                        // Shopify's Admin API access token and Dev Dashboard
                        // client secret are write-only secrets — render them
                        // masked like other tokens.
                        type={
                          (editingMCP?.mcp_slug === SHOPIFY_MCP_SLUG &&
                            (field === SHOPIFY_TOKEN_FIELD ||
                              field === SHOPIFY_CLIENT_SECRET_FIELD)) ||
                          isMcpSecretField(field, editingMCP)
                            ? 'password'
                            : 'text'
                        }
                        changeListeners={[
                          (event) => {
                            setEditingMCP({
                              ...editingMCP,
                              credentials: {
                                ...editingMCP?.credentials,
                                [field]:
                                  field === 'MYSHOPIFY_DOMAIN'
                                    ? removeHttpPrefix(event.target.value)
                                    : event.target.value,
                              },
                            });
                          },
                        ]}
                        // Normalize "acme" / a pasted URL to
                        // "acme.myshopify.com" once the field loses focus (the
                        // submit handler normalizes + validates again).
                        blurListeners={
                          editingMCP?.mcp_slug === SHOPIFY_MCP_SLUG &&
                          field === SHOPIFY_DOMAIN_FIELD
                            ? [
                                () => {
                                  const current =
                                    editingMCP?.credentials?.[
                                      SHOPIFY_DOMAIN_FIELD
                                    ] || '';
                                  const normalized =
                                    normalizeShopifyDomain(current);
                                  if (normalized !== current) {
                                    setEditingMCP({
                                      ...editingMCP,
                                      credentials: {
                                        ...editingMCP?.credentials,
                                        [SHOPIFY_DOMAIN_FIELD]: normalized,
                                      },
                                    });
                                  }
                                },
                              ]
                            : undefined
                        }
                        id={`${field}-input`}
                        value={editingMCP?.credentials?.[field] || ''}
                        placeholder={
                          field === 'MANAGER_ACCOUNT_ID' &&
                          editingMCP?.mcp_slug === 'google_ads'
                            ? `${convertSnakeCaseToTitleCase(field)} (${t(
                                'Optional'
                              )})`
                            : isTriplewhaleStoreField(
                                editingMCP?.mcp_slug,
                                field
                              )
                            ? t('MCP-TriplewhaleStoreLabel')
                            : // Friendly labels for the legacy uppercase
                            // Shopify credential keys.
                            editingMCP?.mcp_slug === SHOPIFY_MCP_SLUG &&
                              field === SHOPIFY_DOMAIN_FIELD
                            ? t('Shopify-DomainLabel')
                            : editingMCP?.mcp_slug === SHOPIFY_MCP_SLUG &&
                              field === SHOPIFY_TOKEN_FIELD
                            ? t('Shopify-TokenLabel')
                            : editingMCP?.mcp_slug === SHOPIFY_MCP_SLUG &&
                              field === SHOPIFY_CLIENT_ID_FIELD
                            ? t('Shopify-ClientIdLabel')
                            : editingMCP?.mcp_slug === SHOPIFY_MCP_SLUG &&
                              field === SHOPIFY_CLIENT_SECRET_FIELD
                            ? t('Shopify-ClientSecretLabel')
                            : mcpFieldLabel(field)
                        }
                        required={
                          !isOptionalMcpField(editingMCP?.mcp_slug, field) &&
                          // A configured Shopify secret (access token /
                          // client secret) may be left blank to keep the
                          // stored value — so it is not required on an
                          // existing connection.
                          !(
                            editingMCP?.mcp_slug === SHOPIFY_MCP_SLUG &&
                            (field === SHOPIFY_TOKEN_FIELD ||
                              field === SHOPIFY_CLIENT_SECRET_FIELD) &&
                            editingMCP?.is_active !== undefined
                          ) &&
                          // Any other secret the connection already stores
                          // is kept when left blank.
                          !isSavedMcpSecret(field, editingMCP)
                        }
                        infoBanner={
                          isTriplewhaleStoreField(editingMCP?.mcp_slug, field)
                            ? t('MCP-TriplewhaleStoreHint')
                            : undefined
                        }
                        innerPlaceholder={
                          editingMCP?.mcp_slug !== SHOPIFY_MCP_SLUG &&
                          isSavedMcpSecret(field, editingMCP)
                            ? t('MCP-SecretSavedKeepBlank')
                            : isTriplewhaleStoreField(
                                editingMCP?.mcp_slug,
                                field
                              )
                            ? 'my-store.myshopify.com'
                            : editingMCP?.mcp_slug === SHOPIFY_MCP_SLUG &&
                          field === SHOPIFY_TOKEN_FIELD
                            ? editingMCP?.is_active !== undefined
                              ? t('Shopify-TokenConfigured')
                              : t(`EnterYour`) +
                                ' ' +
                                t('Shopify-TokenLabel') +
                                ' ("shpat_…")'
                            : editingMCP?.mcp_slug === SHOPIFY_MCP_SLUG &&
                              field === SHOPIFY_CLIENT_SECRET_FIELD
                            ? editingMCP?.is_active !== undefined
                              ? t('Shopify-SecretConfigured')
                              : t(`EnterYour`) +
                                ' ' +
                                t('Shopify-ClientSecretLabel')
                            : editingMCP?.mcp_slug === SHOPIFY_MCP_SLUG &&
                              field === SHOPIFY_CLIENT_ID_FIELD
                            ? t(`EnterYour`) + ' ' + t('Shopify-ClientIdLabel')
                            : editingMCP?.mcp_slug === SHOPIFY_MCP_SLUG &&
                              field === SHOPIFY_DOMAIN_FIELD
                            ? t(`EnterYour`) +
                              ' ' +
                              t('Shopify-DomainLabel') +
                              ' ("example.myshopify.com")'
                            : t(`EnterYour`) +
                              ' ' +
                              mcpFieldLabel(field) +
                              (field === 'MYSHOPIFY_DOMAIN'
                                ? ' ("example.myshopify.com")'
                                : '')
                        }
                        pattern={
                          (field === 'MANAGER_ACCOUNT_ID' ||
                            field === 'ACCOUNT_ID') &&
                          editingMCP?.mcp_slug === 'google_ads'
                            ? '^[0-9]+$'
                            : editingMCP?.mcp_slug === SHOPIFY_MCP_SLUG &&
                              field === SHOPIFY_TOKEN_FIELD
                            ? '^shpat_.+$'
                            : editingMCP?.mcp_slug === SHOPIFY_MCP_SLUG &&
                              field === SHOPIFY_CLIENT_ID_FIELD
                            ? '^[A-Za-z0-9_-]{12,}$'
                            : undefined
                        }
                        title={
                          (field === 'MANAGER_ACCOUNT_ID' ||
                            field === 'ACCOUNT_ID') &&
                          editingMCP?.mcp_slug === 'google_ads'
                            ? t('OnlyNumeric-validation')
                            : editingMCP?.mcp_slug === SHOPIFY_MCP_SLUG &&
                              field === SHOPIFY_TOKEN_FIELD
                            ? t('Shopify-TokenValidation')
                            : editingMCP?.mcp_slug === SHOPIFY_MCP_SLUG &&
                              field === SHOPIFY_CLIENT_ID_FIELD
                            ? t('Shopify-ClientIdValidation')
                            : editingMCP?.mcp_slug === SHOPIFY_MCP_SLUG &&
                              field === SHOPIFY_DOMAIN_FIELD
                            ? t('Shopify-DomainValidation')
                            : undefined
                        }
                      />
                    )}
                  </div>
                )
              )}
            </div>
            <div className='mcpConfigBox__content--info'>
              {isNotEmptyString(modalErrorMessage) && (
                <p
                  role='alert'
                  data-testid='mcp-modal-error'
                  style={{
                    marginTop: '1rem',
                    padding: '0.8rem 1rem',
                    fontSize: '1.1rem',
                    borderRadius: '0.5rem',
                    color: 'var(--danger-text-color)',
                    background: 'var(--danger-background-color)',
                    border: '1px solid var(--danger-border-color)',
                  }}
                >
                  {modalErrorMessage}
                </p>
              )}
              <p
                style={{
                  marginTop: '1rem',
                  fontSize: '1rem',
                  fontStyle: 'italic',
                  color: '#7a7a7a',
                }}
              >
                {isActivationToggleConfirmationVisible
                  ? ' * ' + t('MCP-ActivationToggleConfirmation')
                  : ''}
              </p>
            </div>
          </div>
          <div className='mcpConfigBox__buttons'>
            {editingMCP?.is_active !== undefined ? (
              <>
                {!editingMCP?.is_default && (
                  <MainSquareButton
                    value={
                      isDeleting ? `${t('Disconnecting')}...` : t('Disconnect')
                    }
                    disabled={isSubmitting || isDeleting}
                    type='button'
                    form='mcpModal-form-update'
                    onClick={deleteHandler}
                    classes='mcpConfigBox__buttons--danger'
                    icon={crossSVG}
                  />
                )}
                <MainSquareButton
                  value={
                    isSubmitting
                      ? `${t('Updating')}...`
                      : !editingMCP?.is_default &&
                        editingMCP?.oauth_required &&
                        isNotEmptyString(editingMCP?.oauth_url)
                      ? t('Authorize')
                      : t('UpdateMCP')
                  }
                  disabled={isSubmitting || isDeleting}
                  type='submit'
                  form='mcpModal-form-update'
                  classes='mcpConfigBox__buttons--update'
                  icon={settingSVG}
                />
              </>
            ) : (
              <MainSquareButton
                value={isSubmitting ? `${t('Connecting')}...` : t('ConnectMCP')}
                disabled={isSubmitting || isDeleting}
                type='submit'
                classes='mcpConfigBox__buttons--update'
                form='mcpModal-form-update'
                icon={saveSVG}
              />
            )}
          </div>
        </form>
      </Popup>

      {/* Leaving the app for a provider's consent screen is a full-page
          navigation the user never asked for explicitly — confirm it first.
          Inline styles on purpose: the SCSS bundle is global and generic
          class names collide across pages. */}
      <Popup
        styles={{ backgroundColor: '#fff', maxWidth: '44rem' }}
        setTrigger={() => setPendingOauthMCP(undefined)}
        trigger={pendingOauthMCP !== undefined}
      >
        <div style={{ padding: '0.5rem 0.5rem 0' }}>
          <h1 style={{ fontSize: '1.6rem', marginBottom: '1rem' }}>
            {t('MCP-OauthRedirectConfirmTitle', {
              mcpName: makeFirstLetterCapital(pendingOauthMCP?.mcp_name),
            })}
          </h1>
          <p style={{ fontSize: '1.1rem', lineHeight: 1.5 }}>
            {t('MCP-OauthRedirectConfirmText', {
              mcpName: makeFirstLetterCapital(pendingOauthMCP?.mcp_name),
            })}
          </p>
          <div
            style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'flex-end',
              marginTop: '2rem',
            }}
          >
            <MainSquareButton
              value={t('Cancel')}
              type='button'
              buttonType='white'
              normal
              onClick={() => setPendingOauthMCP(undefined)}
            />
            <MainSquareButton
              value={t('MCP-OauthRedirectConfirmContinue')}
              type='button'
              normal
              icon={linkSVG}
              onClick={() => {
                const mcp = pendingOauthMCP;
                setPendingOauthMCP(undefined);
                performOauthRedirect(mcp);
              }}
            />
          </div>
        </div>
      </Popup>

      <DisconnectionReasonPopup
        isOpen={isDisconnectionReasonPopupOpen}
        onClose={() => {
          setIsDisconnectionReasonPopupOpen(false);
        }}
        onSubmit={handleDisconnectionReasonSubmit}
        onGoBack={handleGoBack}
        isSubmitting={isSubmittingReason}
      />
    </>
  );
};

export default MCP;
